/**
 * SliderComparison - Slider-based image comparison view
 *
 * Uses CSS clip-path to reveal/hide portions of the overlay image
 * as the slider is dragged. Uses AdaptivePhoto for intelligent resolution selection.
 *
 * Feature: 081-photo-versioning (T052)
 */

import { cn } from '@/lib/utils';
import { ComparisonHandle } from './ComparisonHandle';
import { useComparisonSlider } from './useComparisonSlider';
import { AdaptivePhoto, type AdaptivePhotoProps } from '@/components/photos/AdaptivePhoto';

export interface SliderComparisonProps {
  /** Photo object for the "before" image (underneath) */
  beforePhoto: AdaptivePhotoProps['photo'];
  /** Photo object for the "after" image (on top, clipped) */
  afterPhoto: AdaptivePhotoProps['photo'];
  /** Alt text for before image */
  beforeAlt?: string;
  /** Alt text for after image */
  afterAlt?: string;
  /** Label for before image (e.g., "v1") */
  beforeLabel?: string;
  /** Label for after image (e.g., "v2") */
  afterLabel?: string;
  /** Initial slider position (0-100) */
  initialPosition?: number;
  /** Orientation of the slider */
  orientation?: 'horizontal' | 'vertical';
  /** Callback when position changes */
  onPositionChange?: (position: number) => void;
  /** Optional className */
  className?: string;
}

/**
 * Slider comparison component with clip-path overlay
 */
export function SliderComparison({
  beforePhoto,
  afterPhoto,
  beforeAlt = 'Before',
  afterAlt = 'After',
  beforeLabel = 'Before',
  afterLabel = 'After',
  initialPosition = 50,
  orientation = 'horizontal',
  onPositionChange,
  className,
}: SliderComparisonProps) {
  const {
    position,
    isDragging,
    containerRef,
    handlePointerDown,
    handleKeyDown,
  } = useComparisonSlider({
    initialPosition,
    orientation,
    onPositionChange,
  });

  const isHorizontal = orientation === 'horizontal';

  // Calculate clip-path for both images
  // "before" shows the left/top portion, "after" shows the right/bottom portion
  const beforeClipPath = isHorizontal
    ? `inset(0 ${100 - position}% 0 0)` // Show left portion
    : `inset(0 0 ${100 - position}% 0)`; // Show top portion

  const afterClipPath = isHorizontal
    ? `inset(0 0 0 ${position}%)` // Show right portion
    : `inset(${position}% 0 0 0)`; // Show bottom portion

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative overflow-hidden select-none',
        // Disable text selection during drag
        isDragging && 'cursor-ew-resize',
        className
      )}
    >
      {/* Before image (left/top portion) - clipped to avoid showing through different aspect ratio */}
      <AdaptivePhoto
        key={`before-${beforePhoto.id}`}
        photo={beforePhoto}
        alt={beforeAlt}
        className="w-full h-full"
        imgClassName="w-full h-full object-contain"
        imgStyle={{ clipPath: beforeClipPath }}
        trackingSource="version_compare_slider"
        preferStorageUrl
      />

      {/* After image (right/bottom portion, absolutely positioned) */}
      {/* Wrap in absolute div since AdaptivePhoto's container has 'relative' which would conflict */}
      <div className="absolute inset-0 w-full h-full">
        <AdaptivePhoto
          key={`after-${afterPhoto.id}`}
          photo={afterPhoto}
          alt={afterAlt}
          className="w-full h-full"
          imgClassName="w-full h-full object-contain"
          imgStyle={{ clipPath: afterClipPath }}
          trackingSource="version_compare_slider"
          preferStorageUrl
        />
      </div>

      {/* Slider handle */}
      <ComparisonHandle
        position={position}
        orientation={orientation}
        isDragging={isDragging}
        onPointerDown={handlePointerDown}
        onKeyDown={handleKeyDown}
        ariaLabel={`Compare images: ${Math.round(position)}% visible`}
      />

      {/* Version labels */}
      <div
        className={cn(
          'absolute z-5 pointer-events-none',
          isHorizontal
            ? 'top-4 left-4'
            : 'top-4 left-4'
        )}
      >
        <span className="px-2 py-1 bg-black/70 text-white text-xs rounded">
          {beforeLabel}
        </span>
      </div>
      <div
        className={cn(
          'absolute z-5 pointer-events-none',
          isHorizontal
            ? 'top-4 right-4'
            : 'bottom-4 right-4'
        )}
      >
        <span className="px-2 py-1 bg-black/70 text-white text-xs rounded">
          {afterLabel}
        </span>
      </div>
    </div>
  );
}
